// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/enterprise-admin/connectors/ConnectorDetailDrawer.jsx

import React from "react";
import ConnectorOwnerPanel from "./ConnectorOwnerPanel";
import OperationalCaptureAction from "../../operational-capture/OperationalCaptureAction";

function dateLabel(value) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function titleLabel(value) {
  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function lifecycleTone(state) {
  const normalized = String(state || "not_configured").toLowerCase();
  if (normalized === "healthy") return "passed";
  if (normalized === "disconnected") return "disconnected";
  if (normalized === "degraded" || normalized === "reauthorization_required") return "failed";
  return "warning";
}

function actionAvailable(connector, action) {
  const actions = Array.isArray(connector?.available_actions)
    ? connector.available_actions
    : [];
  return actions.includes(action);
}

function collectionSummary(connector) {
  const summary = connector?.last_successful_collection_summary;
  if (!summary || typeof summary !== "object") return [];

  return Object.entries(summary)
    .filter(([key]) => key !== "stream")
    .map(([key, value]) => ({
      key,
      label: titleLabel(key),
      value: typeof value === "boolean" ? (value ? "Yes" : "No") : value,
    }));
}

export default function ConnectorDetailDrawer({
  connector,
  health,
  healthLoading,
  saving,
  statusLabel,
  onHealth,
  onReverify,
  onDisconnect,
  onReconnect,
  onPause,
  onResume,
  onOwner,
  onGovernance,
  captureApiBaseUrl,
  captureGetAccessToken,
  captureTenantId,
  captureUserId,
}) {
  if (!connector) {
    return (
      <aside className="enterprise-admin-detail-panel enterprise-admin-connector-detail">
        <p className="enterprise-admin-eyebrow">Connector Detail</p>
        <h3>Select a connector</h3>
        <p className="enterprise-admin-muted">
          Review live authorization, health, ownership, and governance actions.
        </p>
      </aside>
    );
  }

  const lifecycleState = connector.lifecycle_state || "not_configured";
  const lifecycleLabel = connector.status_label || titleLabel(lifecycleState);
  const summaryTone = lifecycleTone(lifecycleState);
  const isPaused = lifecycleState === "paused";
  const isDisconnected = lifecycleState === "disconnected";
  const isConfigured = Boolean(connector.connector_id);

  const verifiedAccount =
    health?.verified_account?.name ||
    health?.verified_account?.reference ||
    connector?.verified_account?.display ||
    connector?.verified_account?.name ||
    connector?.verified_account?.reference ||
    "Not yet verified";

  const owner = {
    user_id: connector?.owner?.user_id || connector?.owner_user_id || null,
    name: connector?.owner?.name || connector?.owner_name || null,
    email: connector?.owner?.email || connector?.owner_email || null,
  };

  const ownerConnector = {
    ...connector,
    owner_user_id: owner.user_id,
    owner_name: owner.name,
    owner_email: owner.email,
  };

  const lastVerified = health?.verified_at || connector.last_verified_at;
  const lastHealthCheck =
    health?.last_health_check_at || connector.last_health_check_at;
  const collectedAt = connector.last_successful_collection_at;
  const collectedStream = connector.last_successful_stream;
  const summaryRows = collectionSummary(connector);

  const canHealth = actionAvailable(connector, "check_health");
  const canReverify = actionAvailable(connector, "reverify");
  const canDisconnect = actionAvailable(connector, "disconnect");
  const canReconnect = actionAvailable(connector, "reconnect");
  const canPause = actionAvailable(connector, "pause");
  const canResume = actionAvailable(connector, "resume");
  const canOwner = actionAvailable(connector, "assign_owner");
  const canGovernance = actionAvailable(connector, "update_governance");

  return (
    <aside className="enterprise-admin-detail-panel enterprise-admin-connector-detail">
      <div className="enterprise-admin-detail-heading">
        <p className="enterprise-admin-eyebrow">Connector Detail</p>
        <h3>{connector.provider_name || connector.provider_key || "Connector"}</h3>
        <p className="enterprise-admin-muted">
          {connector.business_purpose || connector.description || "No business purpose recorded yet."}
        </p>
      </div>

      <section className="enterprise-admin-connector-lifecycle-section">
        <div className="enterprise-admin-connector-lifecycle-heading">
          <div>
            <p className="enterprise-admin-eyebrow">Operational Status</p>
            <h4>{lifecycleLabel}</h4>
          </div>
          <span className={`enterprise-admin-connector-health-pill status-${summaryTone}`}>
            {lifecycleLabel}
          </span>
        </div>

        <div className={`enterprise-admin-connector-readiness-summary status-${summaryTone}`}>
          <span>{lifecycleState === "healthy" ? "✓" : isDisconnected ? "—" : "!"}</span>
          <div>
            <strong>{connector.status_detail || lifecycleLabel}</strong>
            <p>
              {connector.status_detail ||
                "OASYS is evaluating this provider's current authorization and collection state."}
            </p>
          </div>
        </div>

        <div className="enterprise-admin-connector-lifecycle-actions">
          {canHealth ? (
            <button
              type="button"
              disabled={saving || healthLoading}
              onClick={onHealth}
            >
              {healthLoading ? "Checking…" : "Check health"}
            </button>
          ) : null}

          {canReverify ? (
            <button type="button" disabled={saving} onClick={onReverify}>
              Reverify
            </button>
          ) : null}

          {canDisconnect ? (
            <button
              type="button"
              className="danger"
              disabled={saving}
              onClick={onDisconnect}
            >
              Disconnect
            </button>
          ) : null}

          {canReconnect ? (
            <button
              type="button"
              className="primary"
              disabled={saving}
              onClick={onReconnect}
            >
              Reconnect
            </button>
          ) : null}
        </div>
      </section>

      <dl className="enterprise-admin-definition-list enterprise-admin-connector-definition-list">
        <div>
          <dt>Lifecycle</dt>
          <dd>{lifecycleLabel}</dd>
        </div>
        <div>
          <dt>Authorization</dt>
          <dd>{statusLabel(connector.authorization_status)}</dd>
        </div>
        <div>
          <dt>Governance</dt>
          <dd>{statusLabel(connector.governance_status)}</dd>
        </div>
        <div>
          <dt>Verified account</dt>
          <dd>{verifiedAccount}</dd>
        </div>
        <div>
          <dt>Last verified</dt>
          <dd>{dateLabel(lastVerified)}</dd>
        </div>
        <div>
          <dt>Last health check</dt>
          <dd>{dateLabel(lastHealthCheck)}</dd>
        </div>
        <div>
          <dt>Last collection</dt>
          <dd>{dateLabel(collectedAt)}</dd>
        </div>
        <div>
          <dt>Collection stream</dt>
          <dd>{collectedStream ? titleLabel(collectedStream) : "—"}</dd>
        </div>
      </dl>

      {summaryRows.length ? (
        <section className="enterprise-admin-detail-section">
          <p className="enterprise-admin-eyebrow">Latest Collection</p>
          <div className="enterprise-admin-definition-list enterprise-admin-connector-definition-list">
            {summaryRows.map((item) => (
              <div key={item.key}>
                <dt>{item.label}</dt>
                <dd>{String(item.value)}</dd>
              </div>
            ))}
            {String(connector?.provider_key || connector?.provider || "").toLowerCase() === "aws" ? (
              <OperationalCaptureAction
                label="Capture Now"
                apiBaseUrl={captureApiBaseUrl}
                getAccessToken={captureGetAccessToken}
                tenantId={captureTenantId}
                userId={captureUserId}
                connectorId={connector.connector_id}
                className="enterprise-admin-connector-refresh-button"
              />
            ) : null}
          </div>
        </section>
      ) : null}

      {Array.isArray(health?.checks) && health.checks.length ? (
        <section className="enterprise-admin-detail-section">
          <p className="enterprise-admin-eyebrow">Live Verification Checks</p>
          <div className="enterprise-admin-connector-validation-list">
            {health.checks.map((check) => (
              <article key={check.key} className={`status-${check.status}`}>
                <span>{check.status === "passed" ? "✓" : "!"}</span>
                <div>
                  <strong>{statusLabel(check.key)}</strong>
                  <p>{check.detail}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {isConfigured ? <ConnectorOwnerPanel connector={ownerConnector} /> : null}

      {isConfigured && (canOwner || canGovernance || canPause || canResume) ? (
        <section className="enterprise-admin-connector-governance-actions-section">
          <div className="enterprise-admin-connector-governance-actions-heading">
            <p className="enterprise-admin-eyebrow">Governance Actions</p>
            <p className="enterprise-admin-muted">
              Manage accountability and governance without changing provider permissions.
            </p>
          </div>

          <div className="enterprise-admin-detail-actions enterprise-admin-user-actions enterprise-admin-connector-detail-actions">
            {canOwner ? (
              <button type="button" disabled={saving} onClick={onOwner}>
                Assign owner
              </button>
            ) : null}
            {canGovernance ? (
              <button type="button" disabled={saving} onClick={onGovernance}>
                Update governance
              </button>
            ) : null}
            {canResume || isPaused ? (
              <button type="button" disabled={saving} onClick={onResume}>
                Resume
              </button>
            ) : canPause ? (
              <button type="button" disabled={saving} onClick={onPause}>
                Pause
              </button>
            ) : null}
          </div>
        </section>
      ) : null}
    </aside>
  );
}
